import { ComponentProps, ReactElement, ReactNode, useCallback, useEffect } from 'react';
import {
	__experimentalHStack as HStack,
	__experimentalNavigatorButton as NavigatorButton,
} from '@wordpress/components';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon, chevronRightSmall, menu } from '@wordpress/icons';
import clsx from 'clsx';
import { useTranslate } from 'i18n-calypso';

import SidebarV2, {
	SidebarNavigator,
	SidebarNavigatorMenu, SidebarV2Footer,
	SidebarV2Header,
	SidebarV2Main
} from 'components/external/sidebar-v2';
import AutomatticLogo from 'components/automattic-logo';
import ProfileDropdown from 'components/profile-dropdown';
import feature from 'lib/utils/feature';

const ICON_SIZE = 24;

export function useSidebarToggle() {
	return useCallback(
		( open: boolean ) => {
			document.body.classList.toggle( 'focus-sidebar', open );
		},
		[]
	);
}

// @todo implement submenu support
export default function Sidebar( { children = null }: { children?: ReactNode } ) {
	const location = useLocation();
	const toggleSidebar = useSidebarToggle();
	useEffect( () => {
		toggleSidebar( false );
	}, [ location ] );

	return (
		<SidebarV2 className={ clsx(
			'fixed h-auto left-0 top-[var(--wp-admin--admin-bar--height,0px)] bottom-0 z-50',
			'w-full sm:w-[var(--hpp-sidebar-width)]',
			'overflow-hidden',
			'text-[var(--hpp-color-sidebar-text)] bg-[var(--hpp-color-sidebar-background)]',
			'transition-transform duration-150 ease-in-out',
			'[body:not(.focus-sidebar)_&]:-translate-x-full sm:[body:not(.focus-sidebar)_&]:translate-x-0',
			'[&_svg]:fill-[var(--hpp-sidebar-text-color)]',
		) }>
			<SidebarV2Header className="px-2 py-6">
				<AutomatticLogo size={ 24 } />
			</SidebarV2Header>

			<SidebarV2Main>
				{ children }
			</SidebarV2Main>

			<SidebarV2Footer>
				<ProfileDropdown />
			</SidebarV2Footer>
		</SidebarV2>
	);
}

Sidebar.Navigator = SidebarNavigator;
Sidebar.NavigatorMenu = SidebarNavigatorMenu;

Sidebar.Button = ( { path, to, disabled = false, target, children }: { path: string, to?: string, disabled?: boolean; target?: ComponentProps<typeof NavLink>['target'], children: ReactNode } ) => {
	const SidebarLink = () => (
		<NavLink
			aria-disabled={ disabled }
			to={ to ?? path }
			target={ target }
			end
			className={ ( { isActive } ) => clsx(
				'block p-[calc(7.9px)_12px] border border-transparent text-[var(--hpp-color-sidebar-text)] text-xsm',
				'rounded no-underline',
				'hover:bg-[var(--hpp-color-sidebar-menu-hover-background)] hover:text-[var(--hpp-color-sidebar-menu-hover-text)]',
				'focus:shadow-none focus:text-[var(--hpp-color-sidebar-text)]',
				'focus-visible:shadow-[0_0_0_1px_var(--hpp-color-sidebar-text-alternative)]',
				'outline outline-offset-0 outline-2 outline-transparent',
				{
					'bg-[var(--hpp-color-sidebar-menu-selected-background)] text-[var(--hpp-color-sidebar-menu-selected-text)]': isActive,
					'pointer-events-none opacity-50': disabled,
				}
			) }
		>
			<HStack justify="flex-start">
				{ children }
			</HStack>
		</NavLink>
	);

	return (
		<li className="[&:not(:last-child)>a]:mb-[2px]">
			<NavigatorButton as={ SidebarLink } path={ path }/>
		</li>
	);
}

Sidebar.ButtonIcon = ( { icon }: { icon: ReactElement } ) => (
	<Icon
		className="text-[var(--hpp-color-sidebar-gridicon-fill)]"
		style={ { fill: 'currentcolor' } }
		icon={ icon }
		size={ ICON_SIZE }
	/>
);

Sidebar.ButtonChevron = () => ( <Icon icon={ chevronRightSmall } size={ ICON_SIZE }/> );

export function SidebarToggle( { className }: { className?: string } ) {
	const translate = useTranslate();
	const toggle = useSidebarToggle();

	// The redesigned shell has its own toggle in the header.
	if ( feature( 'wpcloud-redesign' ) ) {
		return null;
	}

	return (
		<button
			type="button"
			className={ clsx( '[.layout--without-sidebar_&]:hidden sm:hidden ml-auto', className ) }
			aria-label={ translate( 'Open navigation menu' ) as string }
			onClick={ () => toggle( true ) }
		>
			<Icon icon={ menu }/>
		</button>
	);
};
